<style scoped>
.mc-card {
	border-radius: 0.75rem;
	transition:
		border-color 0.2s ease,
		box-shadow 0.2s ease;
}
.mc-card.card-present {
	border-color: #0d6efd;
	box-shadow: 0 2px 12px rgba(13, 110, 253, 0.15);
}
.mc-card.card-clear {
	border-color: #198754;
}
.mc-card-large .mc-media {
	width: 84px;
	height: 84px;
	border-radius: 1rem;
}
.mc-card-large .mc-media i {
	font-size: 2.5rem;
}
.mc-card-large h4 {
	font-size: 1.35rem;
}
.mc-card-large .mc-desc {
	font-size: 0.95rem;
}
/* Guided step: the card fills the available height; the hero image flexes so
   the whole card fits without scrolling on typical phone viewports. */
.mc-card-large .card-body {
	height: 100%;
	padding: 0.75rem;
}
.mc-card-large .mc-hero {
	aspect-ratio: auto;
	flex: 1 1 auto;
	min-height: 72px;
	max-height: 42vh;
	object-fit: cover;
	margin-bottom: 0.5rem;
}
.mc-media {
	width: 56px;
	height: 56px;
	border-radius: 0.75rem;
	background-color: #e9f2fb;
	color: #0d6efd;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}
.mc-media i {
	font-size: 1.75rem;
}
.mc-hero {
	width: 100%;
	aspect-ratio: 4 / 3;
	object-fit: cover;
	border-radius: 0.5rem;
	margin-bottom: 0.75rem;
	background-color: #e9f2fb;
}
.mc-preline {
	white-space: pre-line;
}
</style>
<template>
	<article
		class="card h-100 mc-card"
		:class="{
			'card-present': isPresent,
			'card-clear': isClear,
			'mc-card-large': large,
		}"
	>
		<div class="card-body d-flex flex-column">
			<img
				v-if="showImage"
				:src="mediaUrl"
				class="mc-hero"
				:alt="label"
				loading="lazy"
				@error="onMediaError"
			/>
			<div v-else class="d-flex mb-2">
				<div class="mc-media me-3">
					<i :class="icon"></i>
				</div>
			</div>
			<h4 class="h6 mb-1">{{ label }}</h4>
			<p class="small text-muted mb-0 mc-desc">{{ description }}</p>

			<div v-if="isPresent" class="alert alert-light border mb-0 mt-2 py-2">
				<strong class="small d-block mb-1">
					<i class="bi bi-tools me-1"></i>{{ t("solution_label") }}
				</strong>
				<p class="mb-0 mc-preline small">{{ solution }}</p>
				<a
					v-if="solutionLink"
					:href="solutionLink.href"
					class="small d-block mt-1"
					target="_blank"
					rel="noopener noreferrer"
					>{{ solutionLink.label }}</a
				>
			</div>

			<!-- Inline tools (list mode). The guided walk renders the tools in
			     the fixed bottom bar instead (display-only card). -->
			<HabitatTools
				v-if="!displayOnly"
				:habitat="habitat"
				:token="token"
				class="mt-2"
			/>
		</div>
	</article>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { useOrg, useT } from "@/rmo/content/mosquito/copy";
import { habitatCopy, replaceTokens } from "@/rmo/content/mosquito/habitatCopy";
import HabitatTools from "@/rmo/content/mosquito/HabitatTools.vue";
import { useStoreVirtualInspection } from "@/rmo/store/virtualinspection";
import type { VirtualInspectionHabitat } from "@/type/api";

// Tasteful placeholder icons per habitat (used when the photo asset is
// missing). Keyed by taxonomy habitat_key.
const HABITAT_ICONS: Record<string, string> = {
	containers: "bi-bucket",
	plant_saucer: "bi-flower1",
	pet_animal_water: "bi-cup",
	bird_bath: "bi-feather",
	tires: "bi-record-circle",
	tarp_cover: "bi-layers",
	rain_gutter: "bi-house",
	plant_axil: "bi-flower2",
	pool_spa: "bi-water",
	water_feature: "bi-droplet",
	low_spots: "bi-arrow-down-up",
	yard_drain: "bi-square",
	appliance: "bi-box-seam",
	utility: "bi-tools",
	misc_water: "bi-droplet-half",
};

interface Props {
	habitat: VirtualInspectionHabitat;
	token: string;
	/** Larger presentation for the one-at-a-time guided step. */
	large?: boolean;
	/** Hide inline tools; the guided walk renders them in a fixed bottom bar. */
	displayOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	large: false,
	displayOnly: false,
});
const store = useStoreVirtualInspection();
const t = useT();
const org = computed(useOrg);
const mediaBroken = ref(false);

const obs = computed(
	() =>
		store.state?.observations.find(
			(o) => o.habitat_key === props.habitat.key,
		) ?? null,
);
const isPresent = computed(() => obs.value?.present === true);
const isClear = computed(() => obs.value?.present === false);
const mediaUrl = computed(() =>
	props.habitat.media ? `/habitats/${props.habitat.media}` : undefined,
);
const showImage = computed(() => !!mediaUrl.value && !mediaBroken.value);
function onMediaError() {
	// Missing asset → icon fallback.
	mediaBroken.value = true;
}
const icon = computed(
	() => HABITAT_ICONS[props.habitat.key] ?? "bi-question-circle",
);
const copy = computed(() => habitatCopy(props.habitat.key));
const label = computed(() => {
	const entry = copy.value;
	if (!entry) return props.habitat.key;
	return store.language === "es"
		? entry.label.es || entry.label.en
		: entry.label.en;
});
const description = computed(() => {
	const entry = copy.value;
	if (!entry) return "";
	return store.language === "es"
		? entry.description.es || entry.description.en
		: entry.description.en;
});
const solution = computed(() => {
	const entry = copy.value;
	if (!entry) return "";
	// Copy is plain text whose lines are separated by real newlines, rendered
	// by .mc-preline (white-space: pre-line); inject the runtime org identity
	// into {district_name}/{district_phone}.
	return replaceTokens(
		store.language === "es"
			? entry.solution.es || entry.solution.en
			: entry.solution.en,
		{
			district_name: org.value.name,
			district_phone: org.value.phone,
		},
	);
});
// Optional per-habitat explainer; rendered as a real anchor (:href), never
// injected into the plain-text solution above.
const solutionLink = computed(() => {
	const entry = copy.value;
	if (!entry?.link) return null;
	const { href, label } = entry.link;
	return {
		href,
		label: store.language === "es" ? label.es || label.en : label.en,
	};
});
</script>
